/**
 * AIImportProcessingStep
 * Live progress UI while the AI worker is running. The useAIImportJob hook
 * polls every 2s; this component renders the progress + per-file states and
 * transitions to the review step once status hits completed_pending_review.
 */

import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Info, RefreshCw, FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { isJobActive, useAIImportJob, useAIImportJobFiles, useCancelAIImportJob } from '@/hooks/useAIImport';
import type { AIImportFileProcessingState } from '@/types/ai-import';

interface AIImportProcessingStepProps {
  jobId: string;
  onReady: () => void;
  onClose: () => void;
}

const stateLabel: Record<AIImportFileProcessingState, string> = {
  queued: 'Queued',
  uploading_to_anthropic: 'Uploading',
  classifying: 'Classifying',
  extracting: 'Extracting',
  matching: 'Matching',
  done: 'Done',
  failed: 'Failed',
  skipped: 'Skipped',
};

const stateVariant: Record<AIImportFileProcessingState, 'gray' | 'blue' | 'green' | 'red' | 'outline'> = {
  queued: 'gray',
  uploading_to_anthropic: 'blue',
  classifying: 'blue',
  extracting: 'blue',
  matching: 'blue',
  done: 'green',
  failed: 'red',
  skipped: 'outline',
};

export function AIImportProcessingStep({ jobId, onReady, onClose }: AIImportProcessingStepProps) {
  const { data: job, isLoading } = useAIImportJob(jobId);
  const { data: files = [] } = useAIImportJobFiles(jobId, { live: isJobActive(job?.status) });
  const cancelJob = useCancelAIImportJob();
  const [confirmCancel, setConfirmCancel] = useState(false);

  // When the worker reports done, auto-advance to the review step
  useEffect(() => {
    if (job?.status === 'completed_pending_review') {
      onReady();
    }
  }, [job?.status, onReady]);

  const handleCancel = async () => {
    try {
      await cancelJob.mutateAsync(jobId);
    } finally {
      onClose();
    }
  };

  if (isLoading || !job) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
        <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading job…</div>
      </div>
    );
  }

  if (job.status === 'failed') {
    return (
      <div className="p-6 space-y-4">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/30">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5 dark:text-red-400" />
            <div className="flex-1">
              <div className="font-semibold text-red-900 dark:text-red-300">Analysis failed</div>
              <div className="mt-1 text-sm text-red-700 dark:text-red-300">{job.error_message ?? 'Unknown error'}</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  if (job.status === 'cancelled') {
    return (
      <div className="p-6 space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          This import was cancelled.
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const pct = Math.min(100, Math.max(0, job.progress_pct ?? 0));

  return (
    <div className="p-6 space-y-5">
      {/* Headline + progress */}
      <div>
        <div className="flex items-center justify-between text-sm mb-2">
          <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
            <RefreshCw className="h-4 w-4 text-primary-600 animate-spin dark:text-primary-400" />
            <span>{job.current_step ?? 'Initializing'}</span>
          </div>
          <span className="text-gray-500 dark:text-gray-400">{pct}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden dark:bg-gray-800">
          <div
            className="h-full bg-primary-500 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        {job.progress_message && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">{job.progress_message}</div>
        )}
      </div>

      {/* Per-file status */}
      <div className="border rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
        {files.map((f) => (
          <div key={f.id} className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 dark:text-gray-500" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm text-gray-900 truncate dark:text-gray-100">{f.file_name}</div>
                {f.classification && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    classified as <span className="font-medium">{f.classification}</span>
                    {typeof f.classification_confidence === 'number' &&
                      ` · ${(f.classification_confidence * 100).toFixed(0)}%`}
                  </div>
                )}
              </div>
            </div>
            <Badge size="sm" variant={stateVariant[f.processing_state]}>
              {stateLabel[f.processing_state]}
            </Badge>
          </div>
        ))}
      </div>

      {/* Footnote and actions */}
      <div className="text-xs text-gray-500 flex items-center gap-2 dark:text-gray-400">
        <Info className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
        Feel free to close this dialog — analysis continues in the background. You'll see the job in AI Imports.
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Run in background</Button>
        <Button
          variant="ghost"
          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          onClick={() => setConfirmCancel(true)}
          disabled={cancelJob.isPending}
        >
          Cancel import
        </Button>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel this import?"
        description="Processing stops and this job is cancelled. Uploaded files are kept, but nothing will be extracted from them."
        confirmLabel="Cancel import"
        cancelLabel="Keep processing"
        variant="danger"
        loading={cancelJob.isPending}
        onConfirm={handleCancel}
      />
    </div>
  );
}
